<template>
  <div class="draft-bills-page glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Vendor', 'Drafts & Captured']" />

    <div class="glass-tab-view glass-tab-view--flush draft-bills-glass-shell">
      <div class="draft-bills-page-stack">
        <div class="draft-bills-view-toolbar">
          <div class="draft-bills-view-toolbar__tabs" role="tablist" aria-label="Draft Bills layout">
            <button
              type="button"
              role="tab"
              class="draft-bills-view-toolbar__tab"
              :class="{ 'draft-bills-view-toolbar__tab--active': viewMode === 'drafts' }"
              :aria-selected="viewMode === 'drafts'"
              @click="setViewMode('drafts')"
            >
              <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
              Drafts
            </button>
            <button
              type="button"
              role="tab"
              class="draft-bills-view-toolbar__tab"
              :class="{ 'draft-bills-view-toolbar__tab--active': viewMode === 'split' }"
              :aria-selected="viewMode === 'split'"
              @click="setViewMode('split')"
            >
              <i class="bi bi-layout-split" aria-hidden="true"></i>
              Split
            </button>
            <button
              type="button"
              role="tab"
              class="draft-bills-view-toolbar__tab"
              :class="{ 'draft-bills-view-toolbar__tab--active': viewMode === 'mail' }"
              :aria-selected="viewMode === 'mail'"
              @click="setViewMode('mail')"
            >
              <i class="bi bi-inbox" aria-hidden="true"></i>
              Inbox
            </button>
            <button
              type="button"
              role="tab"
              class="draft-bills-view-toolbar__tab"
              :class="{ 'draft-bills-view-toolbar__tab--active': viewMode === 'captured' }"
              :aria-selected="viewMode === 'captured'"
              @click="setViewMode('captured')"
            >
              <i class="bi bi-envelope-open" aria-hidden="true"></i>
              Captured
              <span
                v-if="capturedMatchCount > 0"
                class="draft-bills-view-toolbar__tab-badge"
              >
                {{ capturedMatchCount }}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              class="draft-bills-view-toolbar__tab"
              :class="{ 'draft-bills-view-toolbar__tab--active': viewMode === 'history' }"
              :aria-selected="viewMode === 'history'"
              @click="setViewMode('history')"
            >
              <i class="bi bi-clock-history" aria-hidden="true"></i>
              History
            </button>
          </div>
          <div
            v-if="viewMode === 'split' || viewMode === 'mail' || viewMode === 'captured'"
            class="draft-bills-view-toolbar__mail-actions"
          >
            <span
              v-if="mailSourcesConfigured && isLiveSyncMode"
              class="draft-bills-view-toolbar__live-sync"
              title="Live sync — matching mail reloads while this page is open"
            >
              <i class="bi bi-broadcast" aria-hidden="true"></i>
              Live
            </span>
            <button
              v-if="mailSourcesConfigured && !isLiveSyncMode"
              type="button"
              class="draft-bills-view-toolbar__mail-refresh"
              title="Reload mail from configured senders"
              :disabled="refreshingMail"
              @click="refreshMailInbox"
            >
              <i class="bi bi-arrow-clockwise" :class="{ spinning: refreshingMail }" aria-hidden="true"></i>
              Refresh
            </button>
            <button
              type="button"
              class="draft-bills-view-toolbar__mail-settings"
              title="Configure bill mail senders for Draft Bills"
              @click="openMailSourcesPage"
            >
              <i class="bi bi-gear" aria-hidden="true"></i>
              Mail setup
              <span v-if="mailSourcesConfigured" class="draft-bills-view-toolbar__mail-settings-badge">
                {{ configuredSenderCount }}
              </span>
            </button>
          </div>
        </div>

        <div
          ref="splitContainer"
          class="draft-bills-split-view"
          :class="{
            'draft-bills-split-view--split': viewMode === 'split' || viewMode === 'captured',
            'draft-bills-split-view--drafts-only': viewMode === 'drafts',
            'draft-bills-split-view--mail-only': viewMode === 'mail' || viewMode === 'history',
            'draft-bills-split-view--resizing': isResizing,
          }"
        >
          <div
            v-show="viewMode === 'drafts' || viewMode === 'split' || viewMode === 'captured'"
            class="draft-bills-split-view__drafts"
            :style="draftsPanelStyle"
          >
            <DraftBillsModernPanel
              :captured-bill-ids="capturedBillIds"
              :selected-bill-id="selectedDraftBillId"
              :selected-bill-ids="selectedDraftBillIds"
              :persist-selection="viewMode === 'captured'"
              @select-bill="selectedDraftBillId = $event"
              @select-bills="selectedDraftBillIds = $event"
            />
          </div>

          <div
            v-if="viewMode === 'split' || viewMode === 'captured'"
            class="draft-bills-split-view__resizer"
            role="separator"
            aria-orientation="vertical"
            :aria-label="
              viewMode === 'captured'
                ? 'Resize draft bills and captured panels'
                : 'Resize draft bills and inbox panels'
            "
            tabindex="0"
            @mousedown="startResize"
            @keydown="onResizerKeydown"
          >
            <span class="draft-bills-split-view__resizer-grip" aria-hidden="true"></span>
          </div>

          <div
            v-show="viewMode === 'captured'"
            class="draft-bills-split-view__captured"
            :style="capturedPanelStyle"
          >
            <DraftBillsCapturedPanel
              :emails="inboxEmails"
              :mail-connected="mailConnected"
              :fetch-rules="fetchRules"
              :active="viewMode === 'captured'"
              :selected-bill-id="selectedDraftBillId"
              :selected-bill-ids="selectedDraftBillIds"
              @select-bill="selectedDraftBillId = $event"
              @configure-mail="openMailSourcesPage"
              @open-inbox="setViewMode('mail')"
              @open-drafts="setViewMode('drafts')"
              @refresh-mail="refreshMailInbox"
              @matches-updated="onCapturedMatchesUpdated"
            />
          </div>

          <div
            v-show="viewMode === 'history'"
            class="draft-bills-split-view__history"
          >
            <DraftBillsHistoryPanel :active="viewMode === 'history'" />
          </div>

          <!-- Keep mail panel mounted so Captured can reuse loaded inbox messages -->
          <div
            v-show="viewMode === 'split' || viewMode === 'mail'"
            class="draft-bills-split-view__mailbox"
            :style="mailPanelStyle"
          >
            <DraftBillsMailPanel
              ref="mailPanel"
              :fetch-rules="fetchRules"
              @configure-mail="openMailSourcesPage"
              @emails-updated="onInboxEmailsUpdated"
              @connection-updated="onMailConnectionUpdated"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import DraftBillsModernPanel from './DraftBillsModernPanel.vue';
import DraftBillsMailPanel from './DraftBillsMailPanel.vue';
import DraftBillsCapturedPanel from './DraftBillsCapturedPanel.vue';
import DraftBillsHistoryPanel from './DraftBillsHistoryPanel.vue';
import mailFetchSettingsService from '@/services/mailFetchSettings.service';
import logger from '@/utils/logger';
import {
  DEFAULT_MAIL_FETCH_RULES,
  hasConfiguredMailSources,
  parseAddressList,
} from '@/modules/vendor/utils/mailInboxFetchRules';
import '../styles/draft-bills-split.css';
import '../styles/split-panel-shell.css';

const STORAGE_KEY_MODE = 'draft-bills-view-mode';
const STORAGE_KEY_SPLIT = 'draft-bills-split-percent-v3';
const DEFAULT_SPLIT_PERCENT = 34;
const MIN_SPLIT_PERCENT = 20;
const MAX_SPLIT_PERCENT = 80;
const MIN_PANEL_PX = 240;
const VIEW_MODES = ['drafts', 'split', 'mail', 'captured', 'history'];

export default {
  name: 'DraftBills',
  components: {
    BrucdcumHeader,
    DraftBillsModernPanel,
    DraftBillsMailPanel,
    DraftBillsCapturedPanel,
    DraftBillsHistoryPanel,
  },
  data() {
    return {
      viewMode: 'split',
      splitPercent: DEFAULT_SPLIT_PERCENT,
      isResizing: false,
      fetchRules: { ...DEFAULT_MAIL_FETCH_RULES },
      loadingMailConfig: false,
      refreshingMail: false,
      inboxEmails: [],
      mailConnected: false,
      capturedMatchCount: 0,
      capturedBillIds: [],
      selectedDraftBillId: null,
      selectedDraftBillIds: [],
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$route.params.companyId;
    },
    mailSourcesConfigured() {
      return hasConfiguredMailSources(this.fetchRules);
    },
    configuredSenderCount() {
      return parseAddressList(this.fetchRules.fromAddresses).length;
    },
    isLiveSyncMode() {
      return String(this.fetchRules?.syncMode || '').toLowerCase() === 'immediate';
    },
    draftsPanelStyle() {
      if (this.viewMode !== 'split' && this.viewMode !== 'captured') return {};
      return { flex: `0 0 ${this.splitPercent}%` };
    },
    mailPanelStyle() {
      if (this.viewMode !== 'split') return {};
      return { flex: `1 1 ${100 - this.splitPercent}%` };
    },
    capturedPanelStyle() {
      if (this.viewMode !== 'captured') return {};
      return { flex: `1 1 ${100 - this.splitPercent}%` };
    },
  },
  mounted() {
    this.restorePreferences();
    this.applyRouteTabQuery();
    this.loadMailFetchSettings();
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.stopResize);
  },
  activated() {
    this.applyRouteTabQuery();
    this.loadMailFetchSettings();
  },
  watch: {
    '$route.query.tab'() {
      this.applyRouteTabQuery();
    },
    '$route.query.view'() {
      this.applyRouteTabQuery();
    },
  },
  beforeUnmount() {
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.stopResize);
  },
  methods: {
    applyRouteTabQuery() {
      const tab = String(this.$route?.query?.tab || this.$route?.query?.view || '')
        .trim()
        .toLowerCase();
      // Legacy deep link: Submitted now lives under Invites & Notes
      if (tab === 'submitted') {
        const companyId = this.companyId;
        if (companyId && this.$router) {
          this.$router.replace({
            name: 'VendorPortalHub',
            params: { id: String(companyId) },
          }).catch(() => {});
        }
        return;
      }
      if (VIEW_MODES.includes(tab)) {
        this.setViewMode(tab);
      }
    },
    restorePreferences() {
      try {
        const savedMode = localStorage.getItem(STORAGE_KEY_MODE);
        if (VIEW_MODES.includes(savedMode)) {
          this.viewMode = savedMode;
        } else if (savedMode === 'submitted') {
          this.viewMode = 'drafts';
        }
        const savedSplit = parseFloat(localStorage.getItem(STORAGE_KEY_SPLIT));
        if (
          Number.isFinite(savedSplit) &&
          savedSplit >= MIN_SPLIT_PERCENT &&
          savedSplit <= MAX_SPLIT_PERCENT
        ) {
          this.splitPercent = savedSplit;
        }
      } catch {
        // ignore storage errors
      }
    },
    setViewMode(mode) {
      if (!VIEW_MODES.includes(mode)) return;
      this.viewMode = mode;
      try {
        localStorage.setItem(STORAGE_KEY_MODE, mode);
      } catch {
        // ignore
      }
      if (mode === 'captured') {
        this.$nextTick(() => this.refreshMailInbox());
      }
    },
    onInboxEmailsUpdated(emails) {
      this.inboxEmails = Array.isArray(emails) ? emails : [];
    },
    onMailConnectionUpdated(connected) {
      this.mailConnected = !!connected;
    },
    onCapturedMatchesUpdated(payload) {
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        const nextCount = Number(payload.count) || 0;
        const nextIds = Array.isArray(payload.billIds)
          ? payload.billIds.map((id) => String(id))
          : [];
        this.capturedMatchCount = nextCount;
        const prev = Array.isArray(this.capturedBillIds) ? this.capturedBillIds : [];
        const same =
          prev.length === nextIds.length && prev.every((id, i) => id === nextIds[i]);
        if (!same) this.capturedBillIds = nextIds;
        const selected = this.selectedDraftBillId != null ? String(this.selectedDraftBillId) : '';
        if (this.viewMode === 'captured' && nextIds.length && !selected) {
          this.selectedDraftBillId = nextIds[0];
          if (!this.selectedDraftBillIds.length) this.selectedDraftBillIds = [nextIds[0]];
        }
        return;
      }
      this.capturedMatchCount = Number(payload) || 0;
      if (!Array.isArray(this.capturedBillIds)) {
        this.capturedBillIds = [];
      }
    },
    startResize(event) {
      if (this.viewMode !== 'split' && this.viewMode !== 'captured') return;
      event.preventDefault();
      this.isResizing = true;
      document.body.classList.add('draft-bills-split-view--body-resizing');
    },
    onResizeMove(event) {
      if (!this.isResizing || !this.$refs.splitContainer) return;

      const rect = this.$refs.splitContainer.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const total = rect.width;
      if (total <= 0) return;

      const minPercent = Math.max(MIN_SPLIT_PERCENT, (MIN_PANEL_PX / total) * 100);
      const maxPercent = Math.min(MAX_SPLIT_PERCENT, 100 - (MIN_PANEL_PX / total) * 100);
      let next = (offsetX / total) * 100;
      next = Math.max(minPercent, Math.min(maxPercent, next));

      this.splitPercent = Math.round(next * 10) / 10;
    },
    stopResize() {
      if (!this.isResizing) return;
      this.isResizing = false;
      document.body.classList.remove('draft-bills-split-view--body-resizing');
      try {
        localStorage.setItem(STORAGE_KEY_SPLIT, String(this.splitPercent));
      } catch {
        // ignore
      }
    },
    onResizerKeydown(event) {
      if (this.viewMode !== 'split' && this.viewMode !== 'captured') return;
      const step = event.shiftKey ? 5 : 2;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.splitPercent = Math.max(MIN_SPLIT_PERCENT, this.splitPercent - step);
        this.persistSplit();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.splitPercent = Math.min(MAX_SPLIT_PERCENT, this.splitPercent + step);
        this.persistSplit();
      }
    },
    persistSplit() {
      try {
        localStorage.setItem(STORAGE_KEY_SPLIT, String(this.splitPercent));
      } catch {
        // ignore
      }
    },
    openMailSourcesPage() {
      if (!this.companyId) return;
      this.$router.push(`/company/${this.companyId}/dashboard/vendors/mail-sources`);
    },
    async loadMailFetchSettings() {
      if (!this.companyId) return;

      this.loadingMailConfig = true;
      try {
        this.fetchRules = await mailFetchSettingsService.getSettings(this.companyId);
      } catch (error) {
        logger.error('Failed to load mail fetch settings:', error);
        this.fetchRules = { ...DEFAULT_MAIL_FETCH_RULES };
      } finally {
        this.loadingMailConfig = false;
      }
    },
    async refreshMailInbox() {
      if (!this.$refs.mailPanel?.reloadEmails) return;

      this.refreshingMail = true;
      try {
        await this.$refs.mailPanel.reloadEmails();
        if (typeof this.$refs.mailPanel.getEmails === 'function') {
          this.inboxEmails = this.$refs.mailPanel.getEmails();
        }
        if (typeof this.$refs.mailPanel.isConnected === 'function') {
          this.mailConnected = this.$refs.mailPanel.isConnected();
        }
      } finally {
        this.refreshingMail = false;
      }
    },
  },
};
</script>
